import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Alert, useTheme, Button, Chip, Drawer, Stack, FormControl, InputLabel, Select, MenuItem, Skeleton } from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import axios from 'utils/axios';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CancelIcon from '@mui/icons-material/Cancel';
import AddProviderSidebar from './AddProviderSidebar';
import ProviderDetailsSidebar from './ProviderDetailsSidebar';

const ProviderIntake = () => {
    const [providerIntakes, setProviderIntakes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [isAddProviderSidebarOpen, setIsAddProviderSidebarOpen] = useState(false);
    const [isProviderDetailsSidebarOpen, setIsProviderDetailsSidebarOpen] = useState(false);
    const [providerDetailsSidebarId, setProviderDetailsSidebarId] = useState(null);
    const [providerDetailsSidebarSchoolYearId, setProviderDetailsSidebarSchoolYearId] = useState(null);
    const [schoolYears, setSchoolYears] = useState([]);
    const [currentSchoolYearId, setCurrentSchoolYearId] = useState(null);
    const [selectedSchoolYearId, setSelectedSchoolYearId] = useState(null);
    const theme = useTheme();

    const renderStatusChip = (params, colorField) => {
        if (!params.value) return null;

        const color = params.row[colorField];
        return (
            <Chip
                label={params.value}
                size="small"
                sx={{
                    backgroundColor: color ? `${color}20` : 'grey.100',
                    color: color || 'grey.600',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: '24px',
                    '& .MuiChip-label': {
                        lineHeight: 1,
                        paddingTop: '1px',
                        paddingBottom: '1px'
                    }
                }}
            />
        );
    };

    const onProviderDetailsSidebarClose = () => {
        setIsProviderDetailsSidebarOpen(false);
        setProviderDetailsSidebarId(null);
    };

    const openProviderDetailsSidebar = (id, schoolYearId) => {
        setProviderDetailsSidebarId(id);
        setProviderDetailsSidebarSchoolYearId(schoolYearId);
        setIsProviderDetailsSidebarOpen(true);
    };

    const getStatusIcon = (status, margin) => {
        const iconStyles = {
            marginRight: margin === undefined ? 1 : margin,
            fontSize: '1rem',
            verticalAlign: 'middle',
            marginTop: '-2px'
        };

        switch (status?.toLowerCase()) {
            case 'completed':
                return (
                    <CheckCircleIcon
                        sx={{
                            ...iconStyles,
                            color: 'success.main'
                        }}
                    />
                );
            case 'open':
                return (
                    <FiberManualRecordIcon
                        sx={{
                            ...iconStyles,
                            color: 'primary.main',
                            fontSize: '1.2rem'
                        }}
                    />
                );
            case 'canceled':
                return (
                    <CancelIcon
                        sx={{
                            ...iconStyles,
                            color: 'text.disabled'
                        }}
                    />
                );
            default:
                return null;
        }
    };

    const columns = [
        {
            field: 'providerName',
            headerName: 'Provider Name',
            flex: 1.5
        },
        {
            field: 'viventiumStatus',
            headerName: 'Viventium Status',
            flex: 1,
            renderCell: (params) => renderStatusChip(params, 'viventiumStatusColor')
        },
        {
            field: 'petsStatus',
            headerName: 'PETS Status',
            flex: 1,
            renderCell: (params) => renderStatusChip(params, 'petsStatusColor')
        },
        {
            field: 'taarisStatus',
            headerName: 'TAARIS Status',
            flex: 1,
            renderCell: (params) => renderStatusChip(params, 'taarisStatusColor')
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getStatusIcon(params.value)}
                    {params.value}
                </Box>
            )
        }
    ];

    useEffect(() => {
        const fetchSchoolYears = async () => {
            try {
                const { data } = await axios.get('/api/providerintake/schoolyears');
                setSchoolYears(data.schoolYears);
                setCurrentSchoolYearId(data.currentSchoolYearId);
                setSelectedSchoolYearId(data.currentSchoolYearId);
            } catch (error) {
                console.error('Error fetching school years:', error);
            }
        };

        fetchSchoolYears();
    }, []);

    useEffect(() => {
        const fetchProviderIntakes = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/api/providerintake/list?schoolYearId=${selectedSchoolYearId}`);

                setProviderIntakes(response.data);
                setError(false);
            } catch (err) {
                setError(true);
                console.error('Error fetching provider intakes:', err);
            } finally {
                setLoading(false);
            }
        };

        if (!isAddProviderSidebarOpen && !isProviderDetailsSidebarOpen && selectedSchoolYearId) {
            fetchProviderIntakes();
        }
    }, [isAddProviderSidebarOpen, isProviderDetailsSidebarOpen, selectedSchoolYearId]);

    const renderContent = () => {
        if (error) {
            return (
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">Error loading Providers</Alert>
                </Box>
            );
        }

        return (
            <Box sx={{ padding: '8px', flex: 1, overflow: 'auto' }}>
                <DataGrid
                    rows={providerIntakes}
                    columns={columns}
                    getRowId={(row) => row.id}
                    onRowClick={(params) =>
                        openProviderDetailsSidebar(params.id, providerIntakes.find((p) => p.id === params.id)?.schoolYearId)
                    }
                    hideFooterPagination
                    hideFooterSelected
                    RowCount
                    disableSelectionOnClick
                    loading={loading}
                    initialState={{
                        filter: {
                            filterModel: {
                                items: [{ columnField: 'status', operatorValue: 'equals', value: 'Open' }]
                            }
                        }
                    }}
                    localeText={{
                        noRowsLabel: 'No Providers'
                    }}
                    sx={{
                        minWidth: '900px',
                        '& .MuiDataGrid-columnHeaders': {
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            borderBottom: `1px solid ${theme.palette.primary.main}`
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600
                        }
                    }}
                />
            </Box>
        );
    };

    return (
        <Box sx={{ height: 'calc(100vh - 212px)', display: 'flex', flexDirection: 'column' }}>
            <MainCard
                title={
                    <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between" overflow="auto">
                        <Box>Providers</Box>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-end"
                            spacing={{ xs: 1.5, lg: 2 }}
                            paddingTop={0.75}
                            paddingBottom={0.75}
                        >
                            <FormControl fullWidth variant="outlined" sx={{ width: 145 }} size="small">
                                {selectedSchoolYearId ? (
                                    <>
                                        <InputLabel>School Year</InputLabel>
                                        <Select
                                            label="School Year"
                                            value={selectedSchoolYearId || ''}
                                            onChange={(e) => setSelectedSchoolYearId(e.target.value)}
                                        >
                                            {schoolYears?.map((sy) => (
                                                <MenuItem key={sy.id} value={sy.id}>
                                                    {sy.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </>
                                ) : (
                                    <Skeleton variant="rounded" width={145} height={40} />
                                )}
                            </FormControl>

                            <Button
                                color="secondary"
                                variant="contained"
                                onClick={() => setIsAddProviderSidebarOpen(true)}
                                sx={{ width: 145 }}
                            >
                                <AddIcon sx={{ marginRight: '8px' }} />
                                Add Provider
                            </Button>
                        </Stack>
                    </Stack>
                }
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    '& .MuiCardHeader-root': { paddingBottom: 1.75, paddingTop: 1.75 }
                }}
                content={false}
            >
                {renderContent()}
                <Drawer
                    anchor="right"
                    open={isAddProviderSidebarOpen}
                    onClose={() => setIsAddProviderSidebarOpen(false)}
                    PaperProps={{
                        square: false,
                        sx: {
                            padding: '16px',
                            width: Math.min(392, window.innerWidth),
                            overflow: 'auto'
                        }
                    }}
                >
                    <div id="drawer-content">
                        <AddProviderSidebar
                            onClose={() => setIsAddProviderSidebarOpen(false)}
                            openProviderDetailsSidebar={openProviderDetailsSidebar}
                            schoolYears={schoolYears}
                            currentSchoolYearId={currentSchoolYearId}
                        />
                    </div>
                </Drawer>
                <Drawer
                    anchor="right"
                    open={isProviderDetailsSidebarOpen}
                    onClose={() => setIsProviderDetailsSidebarOpen(false)}
                    PaperProps={{
                        square: false,
                        sx: {
                            padding: '16px',
                            width: Math.min(392, window.innerWidth),
                            overflow: 'auto'
                        }
                    }}
                >
                    <div id="drawer-content">
                        <ProviderDetailsSidebar
                            onClose={onProviderDetailsSidebarClose}
                            providerIntakeId={providerDetailsSidebarId}
                            getStatusIcon={getStatusIcon}
                            schoolYearId={providerDetailsSidebarSchoolYearId}
                        />
                    </div>
                </Drawer>
            </MainCard>
        </Box>
    );
};

export default ProviderIntake;
